import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import classNames from 'classnames';
import type { CSSProperties, ReactNode } from 'react';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { IconButton } from '@sorare/core/src/atoms/buttons/IconButton';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { Cards } from '@sorare/core/src/atoms/icons/Cards';
import { CollectionArrow } from '@sorare/core/src/atoms/icons/CollectionArrow';
import { Shirt } from '@sorare/core/src/atoms/icons/shirts/Shirt.tsx';
import { iconInfoCircle } from '@sorare/core/src/atoms/icons/system/iconInfoCircle.ts';
import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex';
import {
  HeadlineM,
  LabelL,
  LabelM,
  LabelS,
} from '@sorare/core/src/atoms/typography';
import { NotMobileAppFeature } from '@sorare/core/src/components/BuyableFeature';
import { CardSetChip } from '@sorare/core/src/components/CardSetChip';
import { CollectionOwner } from '@sorare/core/src/components/collections/CollectionOwner';
import { RankingRatio } from '@sorare/core/src/components/collections/RankingRatio';
import { ScarcityLabel } from '@sorare/core/src/components/collections/ScarcityLabel';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { useIsDesktopAndAbove } from '@sorare/core/src/hooks/device/useIsDesktopAndAbove';
import { getTeamColor } from '@sorare/core/src/lib/collections';
import { fantasy, glossary } from '@sorare/core/src/lib/glossary';
import { useGetCollectionBackground } from '@sorare/core/src/set/hooks.ts';

import { DetailsDialog } from '#src/components/collections/DetailsDialog/index.tsx';
import { LazyAddCollectionToCartButtons } from '#src/components/collections/LazyAddCollectionToCartButtons/index.tsx';
import { ProgressBar } from '#src/components/collections/ProgressBar/index.tsx';
import { useSetHasMultipleSkins } from '#src/hooks/my-cards/useSetHasMultipleSkins.ts';

import type {
  CollectionHeader_cardCollection,
  CollectionHeader_userCardCollection,
} from './__generated__/index.graphql.ts';

const Root = styled(Vertical).attrs({ center: true })`
  position: relative;
  isolation: isolate;
  &.colorBackground {
    background:
      linear-gradient(
        180deg,
        rgba(var(--c-rgb-black), 0.2),
        rgba(var(--c-rgb-black), 0.2)
      ),
      linear-gradient(
        180deg,
        var(--background-color) 0%,
        rgba(var(--c-rgb-black), 0.8) 100%
      );
  }
  &.imageBackground::before {
    content: '';
    position: absolute;
    inset: 0;
    z-index: -1;
    background: var(--collection-background);
    mask-image: linear-gradient(to bottom, var(--c-black) 85%, transparent);
  }
  padding: var(--double-unit);
`;
const CollectionIcon = styled.img`
  width: 120px;
  height: 120px;
  object-fit: contain;
`;
const Content = styled(Vertical).attrs({ center: true, gap: 3 })`
  width: 100%;
  max-width: 600px;
`;
const StatsWrapper = styled(Horizontal)`
  align-self: stretch;
  justify-content: space-around;
`;
const StretchedWrapper = styled.div`
  align-self: stretch;
`;
const ArrowWrapper = styled(Horizontal).attrs({ center: true, as: 'span' })`
  color: var(--c-yellow-400);
`;
const InfoButtonWrapper = styled(SBHorizontal)`
  width: 100%;
  align-items: flex-start;
`;

const CollectionOwnerWrapper = styled.div`
  padding-top: var(--unit);
`;

type Props = {
  userCardCollectionSlug: string;
  userCardCollection?: CollectionHeader_userCardCollection | null;
  cardCollection: Nullable<CollectionHeader_cardCollection>;
  children?: ReactNode;
};
export const CollectionHeader = ({
  userCardCollectionSlug,
  userCardCollection,
  cardCollection,
  children,
}: Props) => {
  const { team, rarity, sportSeason, inSeason, snapshotted, cardSet } =
    cardCollection || {};
  const isDesktopAndAbove = useIsDesktopAndAbove();
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const { currentUser } = useCurrentUserContext();
  const getCollectionBackground = useGetCollectionBackground();

  const displayPlayersCount = useSetHasMultipleSkins(cardSet?.slug);
  if (!team) {
    return null;
  }
  const { pictureUrl, name, styleGuideColor } = team;
  const { name: seasonName } = sportSeason || {};

  const onInfoClick = () => {
    setDetailsDialogOpen(true);
  };

  const {
    liveRanking,
    liveRankingRatio,
    user,
    slotsCount,
    fulfilledSlotsCount,
    ownedPlayersCount,
    totalPlayersCount,
  } = userCardCollection || {};

  const isCurrentUser =
    !!currentUser && currentUser.slug === userCardCollectionSlug;

  const isSpecialSet = cardSet?.slug !== 'common-base-set';
  const headerBackground =
    cardSet?.slug && isSpecialSet
      ? (getCollectionBackground(cardSet.slug, team.slug)?.background ??
        (cardSet.branding?.setBackground
          ? `url(${cardSet.branding.setBackground}) center / cover no-repeat`
          : undefined))
      : undefined;

  return (
    <Root
      style={
        {
          '--background-color': getTeamColor(styleGuideColor),
          '--collection-background': headerBackground,
        } as CSSProperties
      }
      className={classNames({
        colorBackground: !isSpecialSet,
        imageBackground: !!headerBackground,
      })}
    >
      {!snapshotted && (
        <InfoButtonWrapper>
          {isDesktopAndAbove ? (
            <Button onClick={onInfoClick} size="small" color="quaternary">
              <FontAwesomeIcon icon={iconInfoCircle} />
              <FormattedMessage defaultMessage="How it works" />
            </Button>
          ) : (
            <IconButton
              onClick={onInfoClick}
              size="small"
              icon={iconInfoCircle}
              color="quaternary"
            />
          )}
        </InfoButtonWrapper>
      )}
      <Content>
        <Vertical gap={2} center>
          {!!pictureUrl && <CollectionIcon src={pictureUrl} alt="" />}
          <Vertical gap={0} center>
            <HeadlineM>{name}</HeadlineM>
            {!isCurrentUser && !!user && (
              <CollectionOwnerWrapper>
                <CollectionOwner user={user} />
              </CollectionOwnerWrapper>
            )}
          </Vertical>
        </Vertical>
        {cardSet ? (
          <CardSetChip cardSet={cardSet} />
        ) : (
          <LabelM bold>
            <Horizontal gap={0.5} as="span">
              <ScarcityLabel scarcity={rarity || Rarity.custom_series} />
              <span>{seasonName}</span>
            </Horizontal>
          </LabelM>
        )}
        {!!userCardCollection && (
          <StatsWrapper>
            {!!displayPlayersCount && (
              <Vertical gap={0} center>
                <LabelS bold color="var(--c-nd-500)">
                  <FormattedMessage {...glossary.players} />
                </LabelS>
                <Horizontal>
                  <span>
                    <LabelL bold as="span">
                      {ownedPlayersCount}
                    </LabelL>
                    <LabelS as="span" color="var(--c-nd-700)">
                      /{totalPlayersCount}
                    </LabelS>
                  </span>
                  <Shirt />
                </Horizontal>
              </Vertical>
            )}
            <Vertical gap={0} center>
              <LabelS bold color="var(--c-nd-500)">
                <FormattedMessage {...glossary.cards} />
              </LabelS>
              <Horizontal>
                <span>
                  <LabelL bold as="span">
                    {fulfilledSlotsCount}
                  </LabelL>
                  <LabelS as="span" color="var(--c-nd-700)">
                    /{slotsCount}
                  </LabelS>
                </span>
                <Cards />
              </Horizontal>
            </Vertical>
            {!!userCardCollection.withRanking && (
              <Vertical gap={0} center>
                <LabelS bold color="var(--c-nd-500)">
                  <FormattedMessage {...fantasy.rank} />
                </LabelS>
                <LabelL bold>
                  <RankingRatio
                    liveRankingRatio={liveRankingRatio || 0}
                    liveRanking={liveRanking || 0}
                  />
                </LabelL>
              </Vertical>
            )}
            {!snapshotted && (
              <Vertical gap={0} center>
                <LabelS bold color="var(--c-nd-500)">
                  <FormattedMessage {...fantasy.bonus} />
                </LabelS>
                <Horizontal gap={0.5} center>
                  <LabelL bold>+{userCardCollection.bonus * 100}%</LabelL>
                  <ArrowWrapper>
                    <CollectionArrow shiny />
                  </ArrowWrapper>
                </Horizontal>
              </Vertical>
            )}
          </StatsWrapper>
        )}
        {!snapshotted && (
          <StretchedWrapper>
            <ProgressBar
              cardCollection={cardCollection}
              score={userCardCollection?.score || 0}
            />
          </StretchedWrapper>
        )}
        <NotMobileAppFeature>
          {!!cardCollection && !!isCurrentUser && !!rarity && !!inSeason && (
            <LazyAddCollectionToCartButtons
              collectionSlug={cardCollection.slug}
              rarity={rarity}
              userSlug={currentUser.slug}
            />
          )}
        </NotMobileAppFeature>
      </Content>
      {!!children && <StretchedWrapper>{children}</StretchedWrapper>}
      <DetailsDialog
        open={detailsDialogOpen}
        onClose={() => setDetailsDialogOpen(false)}
        cardCollection={cardCollection}
        score={userCardCollection?.score || 0}
      />
    </Root>
  );
};

CollectionHeader.fragments = {
  cardCollection: gql`
    fragment CollectionHeader_cardCollection on CardCollection {
      slug
      rarity
      inSeason
      snapshotted
      cardSet {
        slug
        branding {
          id
          setBackground: assetUrl(slot: PLAY_LINEUP_VIEW_DESKTOP)
        }
        ...CardSetChip_cardSet
      }
      sportSeason {
        name
      }
      team: anyTeam {
        slug
        name
        pictureUrl
        styleGuideColor
      }
      ...ProgressBar_cardCollection
      ...DetailsDialog_cardCollection
    }
    ${ProgressBar.fragments.cardCollection}
    ${DetailsDialog.fragments.cardCollection}
    ${CardSetChip.fragments.cardSet}
  ` as TypedDocumentNode<CollectionHeader_cardCollection>,
  userCardCollection: gql`
    fragment CollectionHeader_userCardCollection on UserCardCollection {
      id
      score
      slotsCount
      fulfilledSlotsCount
      ownedPlayersCount
      totalPlayersCount
      liveRankingRatio
      liveRanking
      withRanking
      bonus
      user {
        slug
        nickname
        ...CollectionOwner_publicUserInfoInterface
      }
    }
    ${CollectionOwner.fragments.publicUserInfoInterface}
  ` as TypedDocumentNode<CollectionHeader_userCardCollection>,
};
